/**
 * Panel strategy definitions and utilities
 * Maps panel types to their behavior and configuration
 */
export const PANEL_STRATEGIES = {
    // Viewing audit metadata (date, site, area, etc.)
    viewSummary: {
        component: 'AuditSummary',
        requiresAudit: true,
        supportsBack: false,
        supportsSubmit: false,
        supportsEdit: false,
    },
    // Editing audit metadata (date, site, area, etc.)
    editSummary: {
        component: 'AuditSummary',
        requiresAudit: true,
        supportsBack: true,
        supportsSubmit: true,
        supportsEdit: false,
    },
    // Uploading an audit from a file (not from a template)
    uploadSummary: {
        component: 'AuditSummary',
        requiresAudit: false,
        supportsBack: false,
        supportsSubmit: true,
        supportsEdit: false,
    },
    // Performing an audit from a template (2 stages tracked by performingAudit flag: false - select template, then true - perform audit)
    performTemplate: {
        component: 'AuditTemplateContent',
        requiresAudit: false,
        supportsBack: true,
        supportsSubmit: false, // Only when performingAudit is true
        supportsEdit: false,
    },
    // Viewing audit template content
    viewTemplate: {
        component: 'AuditTemplateContent',
        requiresAudit: true,
        supportsBack: true,
        supportsSubmit: false,
        supportsEdit: true,
    },
    // Editing audit template content
    editTemplate: {
        component: 'AuditTemplateContent',
        requiresAudit: true,
        supportsBack: true,
        supportsSubmit: true,
        supportsEdit: false,
    },
    // Scheduling an audit
    schedule: {
        component: 'ScheduleAuditForm',
        requiresAudit: false,
        supportsBack: false,
        supportsSubmit: false, // Uses its own form submit
        supportsEdit: false,
    },
};

/**
 * Get strategy for a panel type
 * @param {string} panelType - The panel type
 * @returns {Object|null} - The strategy object or null if not found
 */
export function getPanelStrategy(panelType) {
    return PANEL_STRATEGIES[panelType] || null;
}

/**
 * Check if panel type uses AuditSummary component
 * @param {string} panelType - The panel type
 * @returns {boolean}
 */
export function isSummaryPanel(panelType) {
    const strategy = getPanelStrategy(panelType);
    return strategy?.component === 'AuditSummary';
}

/**
 * Check if panel type uses AuditTemplateContent component
 * @param {string} panelType - The panel type
 * @returns {boolean}
 */
export function isTemplatePanel(panelType) {
    const strategy = getPanelStrategy(panelType);
    return strategy?.component === 'AuditTemplateContent';
}

/**
 * Get the next panel type when going back
 * @param {string} currentPanelType - Current panel type
 * @returns {string|null} - Next panel type or null
 */
export function getBackPanelType(currentPanelType) {
    const backMap = {
        editTemplate: 'viewTemplate',
        viewTemplate: 'viewSummary',
        performTemplate: null, // Special case - handled in component
        editSummary: 'viewSummary',
    };
    return backMap[currentPanelType] || null;
}
